import { WorldMapTooltip } from "./world-map-tooltip";
import { WorldMapCamera } from "./world-map-camera";
import { WorldMapQuality } from "./world-map-quality";
import { WorldMapRoutes } from "./world-map-routes";
import { groupClientLocations } from "./world-map-regions";
import { WorldMapScroll } from "./world-map-scroll";

const SVG_NS = "http://www.w3.org/2000/svg";
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const smoothstep = (value) => { const t = clamp(value); return t * t * (3 - 2 * t); };

export function mountGlobalPresence(root = document) {
	root.querySelectorAll("[data-global-presence]").forEach((element) => {
		if (element.dataset.presenceWatched) return;
		element.dataset.presenceWatched = "true";
		const desktop = window.matchMedia("(min-width: 768px)");
		const mount = () => {
			if (!desktop.matches || element.dataset.presenceMounted) return;
			const locations = JSON.parse(element.querySelector("[data-presence-locations]").textContent);
			if (!locations.length) return;
			element.dataset.presenceMounted = "true";
			new GlobalPresence(element, locations);
			desktop.removeEventListener("change", mount);
		};
		desktop.addEventListener("change", mount);
		mount();
	});
}

class GlobalPresence {
	constructor(element, locations) {
		this.element = element;
		this.locations = locations;
		this.animationEnabled = element.dataset.mapAnimation !== "0";
		this.flagTooltip = new WorldMapTooltip(element);
		this.regions = groupClientLocations(locations);
		const india = locations.find((location) => location.code === "IN");
		this.home = india ? { ...india, codes: ["IN"], label: "OUR TEAM IN GUJARAT", width: 450, height: 250 } : null;
		this.map = element.querySelector(".global-presence__map");
		this.stage = element.querySelector(".global-presence__stage");
		this.sticky = element.querySelector(".global-presence__sticky");
		this.progressBar = element.querySelector(".global-presence__progress span");
		this.camera = new WorldMapCamera(this.map, this.stage, element.querySelector(".global-presence__heading"));
		this.smoothScroll = new WorldMapScroll(element, () => this.schedule());
		this.quality = new WorldMapQuality(() => this.applyQuality());
		this.land = [...this.map.querySelectorAll("[data-country]")];
		this.motion = window.matchMedia("(prefers-reduced-motion: reduce)");
		this.mobile = window.matchMedia("(max-width: 767px)");
		this.compact = window.matchMedia("(max-height: 600px)");
		this.routes = [];
		this.markers = [];
		this.current = -1;
		this.dirty = true;
		this.visible = false;
		this.animationFrame = null;
		this.createNetwork();
		this.routeAnimation = new WorldMapRoutes(this.routes);
		this.routeByCountry = new Map(this.routes.map((route, index) => [route.dataset.route, index]));
		element.querySelector("[data-presence-previous]").addEventListener("click", () => this.navigate(Math.max(0, this.current - 1)));
		element.querySelector("[data-presence-next]").addEventListener("click", () => this.navigate(Math.min(this.regions.length, this.current + 1)));
		this.schedule = () => {
			this.dirty = true;
			this.animateScene();
		};
		window.addEventListener("scroll", () => {
			this.refreshVisibility();
			if (this.visible) this.schedule();
		}, { passive: true });
		window.addEventListener("resize", this.schedule, { passive: true });
		this.motion.addEventListener("change", () => this.configure());
		this.compact.addEventListener("change", () => this.configure());
		this.mobile.addEventListener("change", () => this.configure());
		this.observer = new IntersectionObserver(() => {
			this.refreshVisibility();
			if (this.visible) this.schedule();
		});
		this.observer.observe(element);
		document.addEventListener("visibilitychange", () => {
			this.smoothScroll.setEnabled(this.visible && this.animationEnabled && !this.static && !document.hidden && !this.mobile.matches);
			this.schedule();
		});
		this.configure();
	}

	refreshVisibility() {
		const bounds = this.element.getBoundingClientRect();
		const visible = !this.mobile.matches && bounds.bottom > 0 && bounds.top < innerHeight;
		if (visible === this.visible) return;
		this.visible = visible;
		this.smoothScroll.setEnabled(visible && this.animationEnabled && !this.static && !document.hidden);
		if (!visible) document.body.classList.remove("is-global-presence-active");
		this.animateScene();
	}

	configure() {
		if (this.mobile.matches) {
			this.smoothScroll.setEnabled(false);
			this.animateScene();
			document.body.classList.remove("is-global-presence-active");
			return;
		}
		this.static = this.motion.matches || this.compact.matches;
		this.element.classList.toggle("is-static", this.static);
		this.element.classList.add("is-ready");
		const distance = this.animationEnabled ? this.regions.length * 420 + 600
			: Math.min(600, this.sticky.offsetHeight * 0.65) + 160;
		this.element.style.setProperty("--journey-distance", `${distance}px`);
		this.current = -1;
		this.smoothScroll.setEnabled(this.visible && this.animationEnabled && !this.static && !this.mobile.matches);
		this.applyQuality();
		this.refreshVisibility();
	}

	applyQuality() {
		const mode = this.quality.mode;
		this.element.dataset.mapQuality = mode;
		this.camera.quality = mode;
		this.land.forEach((country) => {
			country.dataset.fullOutline ||= country.getAttribute("d");
			country.setAttribute("d", mode === "full" ? country.dataset.fullOutline : (country.dataset.lightOutline || country.dataset.fullOutline));
		});
		this.smoothScroll.setEnabled(this.visible && this.animationEnabled && !this.static && !this.mobile.matches);
		this.update();
		this.schedule();
	}

	createNetwork() {
		const origin = this.locations.find((location) => location.code === "IN") || this.locations[0];
		this.locations.forEach((location, index) => {
			if (location !== origin) {
				const controlX = (origin.x + location.x) / 2;
				const controlY = Math.max(8, Math.min(origin.y, location.y) - Math.max(26, Math.abs(origin.x - location.x) * 0.2));
				const path = this.svg("path", {
					d: `M${origin.x},${origin.y} Q${controlX},${controlY} ${location.x},${location.y}`,
					pathLength: 1, "data-route": location.code,
					"data-region": this.regions.findIndex((region) => region.codes.includes(location.code)),
				});
				this.map.querySelector(".global-presence__routes").append(path);
				this.routes.push(path);
			}
			const marker = this.svg("g", { transform: `translate(${location.x} ${location.y})`, "data-marker": location.code });
			marker.append(this.svg("circle", { r: 3 }));
			if (location.flag_url) {
				const flag = this.svg("g", { class: "global-presence__flag", transform: `translate(${location.code === "SG" ? 18 : 0} ${location.code === "SG" ? 33 : -31})` });
				flag.append(this.svg("rect", { x: -13, y: -10, width: 26, height: 20, rx: 3 }),
					this.svg("image", { href: location.flag_url, x: -11, y: -8, width: 22, height: 16, preserveAspectRatio: "xMidYMid meet" }));
				this.flagTooltip.bind(flag, location.country);
				marker.append(flag);
			}
			const label = this.svg("text", { y: location.code === "SG" ? 56 : -47, "text-anchor": "middle" });
			label.textContent = location.point_label || location.country;
			marker.append(label);
			this.map.querySelector(".global-presence__markers").append(marker);
			this.markers.push(marker);
		});
	}

	update() {
		if (this.mobile.matches) return;
		const bounds = this.element.getBoundingClientRect();
		const distance = Math.max(1, this.element.offsetHeight - this.sticky.offsetHeight);
		const travelled = Math.max(0, -bounds.top);
		const expansion = this.static ? 1 : clamp(travelled / Math.min(600, this.sticky.offsetHeight * 0.65));
		this.progress = this.static ? 1 : clamp((travelled - Math.min(600, this.sticky.offsetHeight * 0.65)) / Math.max(1, distance - Math.min(600, this.sticky.offsetHeight * 0.65)));
		const index = !this.animationEnabled ? this.regions.length
			: this.static ? (this.current < 0 ? this.regions.length : this.current)
			: Math.min(this.regions.length, Math.floor(this.progress * (this.regions.length + 0.8)));
		this.select(index);
		this.drawNetwork(expansion);
		this.positionMap(index, expansion);
		this.expandFrame(expansion);
		const immersive = !this.static && expansion > 0.98 && bounds.bottom >= this.sticky.offsetHeight - 2;
		document.body.classList.toggle("is-global-presence-active", immersive);
		this.progressBar.style.transform = `scaleX(${this.progress})`;
	}

	expandFrame(progress) {
		if (this.lastExpansion === progress) return;
		this.lastExpansion = progress;
		const eased = smoothstep(progress);
		const inset = (innerWidth < 768 ? 6 : 16) * (1 - eased);
		const frame = this.element.querySelector(".global-presence__frame");
		frame.style.clipPath = progress > 0.995 ? "none" : `inset(${16 * (1 - eased)}% ${inset}% round ${24 * (1 - eased)}px)`;
		const detail = smoothstep((progress - 0.55) / 0.45);
		this.element.style.setProperty("--presence-detail", detail);
		this.element.querySelector(".global-presence__card").inert = detail < 0.95;
		this.element.style.setProperty("--presence-entry", 1 - smoothstep((progress - 0.2) / 0.5));
		this.element.style.setProperty("--presence-entry-scale", 1 + 0.08 * eased);
	}

	select(index) {
		if (this.current === index) return;
		this.current = index;
		const location = this.regions[index] || this.home;
		this.element.querySelector("[data-presence-country]").textContent = location?.country || "Clients around the world";
		this.element.querySelector("[data-presence-label]").textContent = location?.label || "OUR CLIENTS WORLDWIDE";
		this.element.querySelector("[data-presence-description]").textContent = location?.description
			|| `Our clients span ${this.locations.length} countries. Our team supports them from Gandhinagar, Gujarat.`;
		this.element.querySelector("[data-presence-point]").textContent = location?.point_label || (location ? "CLIENT LOCATION" : "CLIENTS AROUND THE WORLD");
		this.element.querySelector("[data-presence-counter]").textContent = `${String(index + 1).padStart(2, "0")} / ${String(this.regions.length + 1).padStart(2, "0")}`;
		const flag = this.element.querySelector("[data-presence-flag]");
		flag.hidden = !location?.flag_url;
		if (location?.flag_url) flag.src = location.flag_url;
		else flag.removeAttribute("src");
		this.element.querySelector("[data-presence-previous]").disabled = index === 0;
		this.element.querySelector("[data-presence-next]").disabled = index === this.regions.length;
		const reached = new Set(this.regions.slice(0, index + 1).flatMap((region) => region.codes));
		const active = new Set(location?.codes || []);
		this.land.forEach((country) => {
			country.classList.toggle("is-reached", country.dataset.country === "IN" || reached.has(country.dataset.country));
			country.classList.toggle("is-current", active.has(country.dataset.country));
		});
		this.markers.forEach((marker) => {
			const code = marker.dataset.marker;
			marker.classList.toggle("is-reached", code === "IN" || reached.has(code));
			marker.classList.toggle("is-region", active.has(code));
			marker.classList.toggle("is-origin", code === "IN");
		});
		this.routes.forEach((route) => route.classList.toggle("is-current", Number(route.dataset.region) === index));
	}

	drawNetwork(expansion) {
		if (expansion < 0.98) {
			this.routeAnimation.setProgress(0, true);
			this.updateFlags();
			return;
		}
		if (!this.animationEnabled) {
			this.routeAnimation.setTogetherProgress(1, this.static);
			if (this.static) this.updateFlags();
			return;
		}
		this.routeAnimation.setProgress(this.static ? this.regions.length + 1
			: this.progress * (this.regions.length + 0.8), this.static);
		if (this.static) this.updateFlags();
	}

	updateFlags() {
		const drawn = this.routeAnimation.rendered;
		const anyReached = drawn.some((reveal) => reveal >= 0.75);
		this.markers.forEach((marker) => {
			const code = marker.dataset.marker;
			const visible = code === "IN" ? anyReached : drawn[this.routeByCountry.get(code)] >= 0.75;
			if (marker.classList.contains("is-flag-visible") === visible) return;
			marker.classList.toggle("is-flag-visible", visible);
			marker.querySelector(".global-presence__flag")?.setAttribute("tabindex", visible ? "0" : "-1");
		});
	}

	positionMap(index, expansion = 1) {
		const location = expansion < 0.98 ? this.home : (this.regions[index] || this.home);
		this.camera.focus(location, !location || !this.animationEnabled, expansion, this.static || !this.animationEnabled);
	}

	animateScene() {
		if (!this.visible || this.static || this.mobile.matches || document.hidden) {
			if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
			this.animationFrame = null;
			this.element.dataset.mapMoving = "false";
			if (this.dirty && !document.hidden) { this.dirty = false; this.update(); }
			return;
		}
		if (this.animationFrame !== null) return;
		this.quality.begin();
		this.element.dataset.mapMoving = "true";
		const tick = (time) => {
			this.smoothScroll.tick(time);
			if (this.dirty) { this.dirty = false; this.update(); }
			this.camera.tick(time);
			this.routeAnimation.tick(time);
			this.updateFlags();
			this.quality.record(time);
			if (this.smoothScroll.moving || this.camera.moving || this.routeAnimation.moving || this.dirty) {
				this.animationFrame = requestAnimationFrame(tick);
			} else {
				this.animationFrame = null;
				this.element.dataset.mapMoving = "false";
			}
		};
		this.animationFrame = requestAnimationFrame(tick);
	}

	navigate(index) {
		const detail = this.element.querySelector(".global-presence__card");
		detail.setAttribute("aria-live", "polite");
		if (this.static) { this.select(index); this.positionMap(index); }
		else {
			const top = window.scrollY + this.element.getBoundingClientRect().top;
			const distance = this.element.offsetHeight - this.sticky.offsetHeight;
			const expansion = Math.min(600, this.sticky.offsetHeight * 0.65);
			const progress = index === this.regions.length ? 1 : (index + 0.5) / (this.regions.length + 0.8);
			const destination = top + expansion + progress * (distance - expansion);
			if (!this.smoothScroll.scrollTo(destination)) window.scrollTo({ top: destination, behavior: "smooth" });
		}
		window.setTimeout(() => detail.setAttribute("aria-live", "off"), 1200);
	}

	svg(tag, attributes) {
		const element = document.createElementNS(SVG_NS, tag);
		Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
		return element;
	}
}
